import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { cleanPayee } from '../utils/formatters';

/**
 * Custom hook for managing Rules and Rule Discovery logic.
 */
export const useRules = (activeProject, user) => {
  const [rules, setRules] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focusAccount, setFocusAccount] = useState('');
  
  // Selection State
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [detailTransactions, setDetailTransactions] = useState([]);
  
  // Auth roles
  const isEditor = user?.type === 'admin' || 
    activeProject?.members?.find(m => m.user === user?._id)?.role === 'editor';

  const fetchRules = useCallback(async () => {
    if (!activeProject?._id) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/projects/${activeProject._id}/rules`);
      setRules(res.data);
    } catch (err) { console.error('Error fetching rules:', err); }
    finally { setLoading(false); }
  }, [activeProject?._id]);

  const fetchSuggestions = useCallback(async (accountId = '') => {
    if (!activeProject?._id) return;
    setLoading(true);
    try {
      const activeAccId = accountId || focusAccount;
      const url = `/api/projects/${activeProject._id}/rules/suggestions` + (activeAccId ? `?accountId=${activeAccId}` : '');
      const res = await axios.get(url);
      setSuggestions(res.data);
    } catch (err) { console.error('Error fetching suggestions:', err); }
    finally { setLoading(false); }
  }, [activeProject?._id, focusAccount]);

  const fetchAccounts = useCallback(async () => {
    if (!activeProject?._id) return;
    try {
      const res = await axios.get(`/api/projects/${activeProject._id}/accounts`);
      setAccounts(res.data);
    } catch (err) { console.error('Error fetching accounts:', err); }
  }, [activeProject?._id]);

  const fetchDetailTransactions = useCallback(async (s) => {
    if (!activeProject?._id || !s) return;
    try {
      const res = await axios.get(`/api/projects/${activeProject._id}/transactions?limit=1000`);
      const allTransactions = res.data.transactions || (Array.isArray(res.data) ? res.data : []);
      const matching = allTransactions.filter(tx => {
        const matchesDesc = !tx.toAccount && cleanPayee(tx.description) === s.description;
        const matchesAccount = focusAccount ? (
          tx.fromAccount && (tx.fromAccount._id === focusAccount || tx.fromAccount === focusAccount)
        ) : true;
        return matchesDesc && matchesAccount;
      });
      setDetailTransactions(matching);
    } catch (err) { console.error('Error fetching detail transactions:', err); }
  }, [activeProject?._id, focusAccount]);

  useEffect(() => {
    if (activeProject?._id) {
      fetchRules();
      fetchAccounts();
    }
  }, [activeProject?._id, fetchRules, fetchAccounts]);

  useEffect(() => {
    if (activeProject?._id) {
      fetchSuggestions(focusAccount);
    }
  }, [activeProject?._id, focusAccount, fetchSuggestions]);

  useEffect(() => {
    if (selectedSuggestion) {
      fetchDetailTransactions(selectedSuggestion);
    } else {
      setDetailTransactions([]);
    }
  }, [selectedSuggestion, focusAccount, fetchDetailTransactions]);

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await axios.delete(`/api/projects/${activeProject._id}/rules/${id}`);
      fetchRules();
    } catch (err) { console.error('Error deleting rule:', err); }
  };

  const applyRule = async (ruleId) => {
    try {
      await axios.post(`/api/projects/${activeProject._id}/rules/${ruleId}/apply`);
      fetchRules();
      fetchSuggestions();
    } catch (err) { console.error('Error applying rule:', err); }
  };

  return {
    rules,
    suggestions,
    accounts,
    isEditor,
    loading,
    selectedSuggestion,
    setSelectedSuggestion,
    detailTransactions,
    fetchRules,
    fetchSuggestions,
    deleteRule,
    applyRule,
    focusAccount,
    setFocusAccount
  };
};
